/**
 * The launch sequence (issue #3; docs/design.md, "Delivery"): register the worker, read the
 * shared state, and decide before anything renders whether this launch installs, applies a
 * staged update or runs; then, once the app is up, check the network for a newer build in the
 * background and stage it. The gate is `decideLaunch`; this module is the glue around it.
 */
import type { IndexRecord } from "../../shared/snapshot-format";
import { listCharacters } from "../character";
import { loadMigrationTarget, runSnapshotMigration } from "../migration";
import { applyStaged, checkForUpdate, DownloadError, installSnapshot, shellCached, type Env } from "./install";
import { notifyStateChanged, registerWorker, updateWorker, waitForControl, workersSupported } from "./register";
import { requestPersistOnce } from "./storage";
import { setUpdateStatus } from "./status";
import type { Installed, PwaState } from "./state";
import { readState } from "./state";
import { decideLaunch, type DownloadProgress, type LaunchDecision } from "./update";

export const BUILD_ID: string = typeof __BUILD_ID__ === "string" ? __BUILD_ID__ : "dev";

export function browserEnv(): Env {
  return { caches, fetch: (url, init) => fetch(url, init), now: () => new Date() };
}

export type LaunchOutcome =
  /** Workers or the Cache API are unavailable: run online, nothing is installed. */
  | { kind: "unsupported" }
  | { kind: "install"; resume: string | null }
  /** The update was applied; the page is reloading. */
  | { kind: "reloading"; from: Installed; to: Installed }
  | { kind: "ready"; active: Installed | null; pending: Installed | null; reason: string | null };

/** The names of the Characters with a Session open. */
export async function sessionsOpen(): Promise<string[]> {
  try {
    return (await listCharacters()).filter((s) => s.sessionOpen).map((s) => s.name);
  } catch {
    return [];
  }
}

export async function decide(env: Env): Promise<LaunchDecision> {
  const state = await readState(env.caches);
  const open = state.staged ? await sessionsOpen() : [];
  const shellReady = state.staged ? await shellCached(env.caches, state.staged.build) : false;
  return decideLaunch(state, open, shellReady);
}

/** The migration hook (#17): every stored Character migrated to the new Snapshot, retired Entries named from the previous index. */
async function migrateAfterApply(previousIndex: unknown): Promise<void> {
  const index = Array.isArray(previousIndex) ? (previousIndex as IndexRecord[]) : undefined;
  try {
    const run = await runSnapshotMigration(await loadMigrationTarget(index));
    if (run.refused) console.warn(run.refused);
  } catch (e) {
    console.warn("the Snapshot migration after the update failed; it runs again from the home screen", e);
  }
}

/** Decide what this launch does and do the part that must happen before the UI: applying a staged update. */
export async function launch(env: Env = browserEnv()): Promise<LaunchOutcome> {
  if (!workersSupported()) {
    setUpdateStatus({ phase: "unsupported" });
    return { kind: "unsupported" };
  }
  void registerWorker();
  const decision = await decide(env);
  if (decision.action === "install") return { kind: "install", resume: decision.resume };
  if (decision.action === "apply") {
    // The worker must serve the new pair before the migration reads the new Snapshot.
    await waitForControl();
    const applied = await applyStaged(env, migrateAfterApply, notifyStateChanged);
    if (applied) {
      location.reload();
      return { kind: "reloading", ...applied };
    }
    return { kind: "ready", active: decision.from, pending: null, reason: null };
  }
  if (decision.pending) setUpdateStatus({ phase: "ready", staged: decision.pending, reason: decision.reason });
  return { kind: "ready", active: decision.active, pending: decision.pending, reason: decision.reason };
}

/** The first install from the install screen: download everything, ask for persistent storage once, wait for the worker. */
export async function install(onProgress: (p: DownloadProgress) => void, env: Env = browserEnv()): Promise<Installed> {
  const active = await installSnapshot(env, BUILD_ID, { onProgress });
  await requestPersistOnce();
  await waitForControl();
  await notifyStateChanged();
  return active;
}

let checking: Promise<void> | null = null;

/** The background check, one at a time; the status store carries the outcome. */
export function checkNow(env: Env = browserEnv()): Promise<void> {
  checking ??= (async () => {
    const at = () => env.now().toISOString();
    setUpdateStatus({ phase: "checking" });
    let release = "";
    try {
      const outcome = await checkForUpdate(
        env,
        { update: updateWorker, ready: (build) => shellCached(env.caches, build) },
        { onProgress: (progress) => setUpdateStatus({ phase: "downloading", progress, release }) },
      );
      if (outcome.kind === "ready") setUpdateStatus({ phase: "ready", staged: outcome.staged, reason: null });
      else if (outcome.kind === "shell-pending") setUpdateStatus({ phase: "shell-pending", release: outcome.deployed.release });
      else if (outcome.kind === "current") setUpdateStatus({ phase: "current", checkedAt: at() });
      else setUpdateStatus({ phase: "idle" });
      await notifyStateChanged();
    } catch (e) {
      if (e instanceof DownloadError && e.offline) setUpdateStatus({ phase: "offline", checkedAt: at() });
      else setUpdateStatus({ phase: "failed", error: (e as Error).message, checkedAt: at() });
    } finally {
      checking = null;
    }
  })();
  return checking;
}

const RECHECK_AFTER_MS = 60 * 60 * 1000;
let scheduled = false;

/** Check shortly after launch and again whenever the app comes back to the foreground an hour later. */
export function scheduleChecks(env: Env = browserEnv(), delayMs = 4000): void {
  if (scheduled || !workersSupported()) return;
  scheduled = true;
  let last = 0;
  const run = () => {
    last = Date.now();
    void checkNow(env);
  };
  setTimeout(run, delayMs);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible" && Date.now() - last > RECHECK_AFTER_MS) run();
  });
}

export async function currentState(env: Env = browserEnv()): Promise<PwaState | null> {
  if (!workersSupported()) return null;
  return readState(env.caches);
}
